import { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow } from "@/components/ui-bits";
import api from "@/lib/api";
import { annotateRegIndent } from "@/lib/regIndent";
import { linkifyEccns, linkifyUsml } from "@/lib/linkifyRefs";
import { ArrowLeft, ExternalLink, AlertTriangle } from "lucide-react";

const REASON_TOOLTIP = {
  AT: "Anti-Terrorism", CB: "Chemical & Biological Weapons", CC: "Crime Control",
  CW: "Chemical Weapons Convention", EI: "Encryption Items", FC: "Firearms Convention",
  MT: "Missile Technology", NP: "Nuclear Nonproliferation", NS: "National Security",
  RS: "Regional Stability", SI: "Significant Items", SL: "Surreptitious Listening",
  SS: "Short Supply", UN: "United Nations Embargo",
};

export default function CCLEntry() {
  const { eccn } = useParams();
  const [entry, setEntry] = useState(null);
  const [meta, setMeta] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setEntry(null);
    setNotFound(false);
    (async () => {
      try {
        const [e, m] = await Promise.all([
          api.get(`/ccl/entries/${encodeURIComponent(String(eccn).toUpperCase())}`),
          api.get("/ccl/meta"),
        ]);
        if (!cancelled) {
          setEntry(e.data);
          setMeta(m.data);
        }
      } catch (err) {
        if (!cancelled) setNotFound(true);
      }
    })();
    return () => { cancelled = true; };
  }, [eccn]);

  const contentHtml = useMemo(() => {
    if (!entry?.content_html) return "";
    // Order matters: linkify ECCNs first (same-tab), then USML refs
    // (new-tab). The USML linkifier skips text already inside <a> so ECCN
    // anchors aren't disturbed.
    let h = linkifyEccns(entry.content_html, { self: entry.eccn });
    h = linkifyUsml(h);
    return h;
  }, [entry]);

  // Annotate rendered paragraphs with indentation levels once the HTML
  // has been injected. CCL uses the BIS "dotted" style (a., a.1., a.1.a).
  useEffect(() => {
    if (!contentHtml) return;
    const el = document.querySelector('[data-testid="ccl-entry-content"]');
    if (el) annotateRegIndent(el);
  }, [contentHtml]);

  // Some ECCNs (e.g. 0A002, 9A005-9A009) are cross-reference stubs with no
  // body text — the "(These items are subject to..." note in heading_full
  // IS the substantive content. Surface it when there's nothing else.
  const headingNote = useMemo(() => {
    if (!entry) return "";
    if (!entry.heading_full || entry.heading_full === entry.heading) return "";
    // Extract the "(These items are..." parenthetical if present.
    const m = entry.heading_full.match(/\(These items are[^)]*\)?/i);
    return m ? m[0] : entry.heading_full.slice(entry.heading.length).trim();
  }, [entry]);

  if (notFound) return <Navigate to="/ccl" replace />;

  const lastMod = entry?.last_modified
    ? new Date(entry.last_modified).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
    : "—";
  const sourceAsOf = meta?.source_as_of || entry?.source_as_of || "—";
  const categoryTitle = meta?.categories?.[entry?.category];

  return (
    <Layout>
      <SEO
        title={entry ? `ECCN ${entry.eccn} — ${entry.heading}` : `ECCN ${eccn}`}
        description={entry ? `Commerce Control List entry ${entry.eccn}: ${entry.heading}. Reasons for control: ${(entry.reasons || []).join(", ")}. Mirrored from eCFR.` : ""}
        path={`/ccl/e/${eccn}`}
      />

      {/* Persistent disclaimer banner — top */}
      <div className="bg-brass-deep/10 border-b border-brass/40" data-testid="ccl-entry-banner-top">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-3 flex items-center gap-3 text-[12px] leading-snug">
          <AlertTriangle className="w-4 h-4 text-brass-deep shrink-0" strokeWidth={2} />
          <p className="text-ink">
            <strong className="uppercase tracking-wide text-[10px]">Mirrored from eCFR{sourceAsOf !== "—" ? ` as of ${sourceAsOf}` : ""}.</strong>{" "}
            This page is a navigational aid, not the authoritative text and not legal advice. Verify against the eCFR.
          </p>
          {entry?.source_url && (
            <a
              href={entry.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] text-ink border-b border-brass/60 hover:text-brass hover:border-brass transition-colors whitespace-nowrap"
              data-testid="ccl-entry-source-top"
            >
              View on eCFR <ExternalLink className="w-3 h-3" strokeWidth={2} />
            </a>
          )}
        </div>
      </div>

      {/* Hero */}
      <section className="border-b border-hairline" data-testid="ccl-entry-hero">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 pt-14 pb-10">
          <Link to={entry ? `/ccl/${entry.category}` : "/ccl"} className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-ink-soft hover:text-brass transition-colors" data-testid="ccl-entry-back">
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
            {categoryTitle ? `Category ${entry.category} — ${stripQuotes(categoryTitle)}` : "All categories"}
          </Link>
          <div className="mt-6 flex flex-wrap items-baseline gap-x-6 gap-y-3">
            <span className="font-mono text-4xl sm:text-5xl font-semibold text-brass-deep" data-testid="ccl-entry-code">
              {entry?.eccn || eccn}
            </span>
            <Eyebrow>ECCN</Eyebrow>
          </div>
          <h1 className="font-serif text-2xl sm:text-3xl text-ink leading-snug tracking-tight mt-4 max-w-3xl" data-testid="ccl-entry-heading">
            {entry?.heading || "Loading…"}
          </h1>
          {entry?.reasons?.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2" data-testid="ccl-entry-reasons">
              {entry.reasons.map((r) => (
                <span
                  key={r}
                  title={REASON_TOOLTIP[r] || r}
                  className="inline-flex items-center gap-2 px-2.5 py-1 text-[11px] font-semibold font-mono tracking-wider uppercase border border-hairline bg-ivory-alt text-ink"
                >
                  <span>{r}</span>
                  <span className="text-ink-soft normal-case font-normal font-sans">{REASON_TOOLTIP[r]}</span>
                </span>
              ))}
            </div>
          )}
          {entry && (
            <p className="mt-6 text-[11px] uppercase tracking-[0.22em] text-ink-soft" data-testid="ccl-entry-last-modified">
              Last modified {lastMod}
            </p>
          )}
        </div>
      </section>

      {/* Content */}
      <section className="max-w-5xl mx-auto px-6 lg:px-8 py-12" data-testid="ccl-entry-body">
        {!entry && <p className="text-ink-soft">Loading full ECCN text…</p>}
        {entry && contentHtml && (
          <div
            className="ccl-entry-content reg-indent"
            dangerouslySetInnerHTML={{ __html: contentHtml }}
            data-testid="ccl-entry-content"
          />
        )}
        {entry && !contentHtml && (
          <div className="ccl-entry-content" data-testid="ccl-entry-content">
            <aside className="border-l-2 border-brass-deep pl-4 py-2 bg-ivory-alt" data-testid="ccl-entry-crossref-note">
              <p className="text-[10px] uppercase tracking-[0.22em] text-brass-deep font-semibold mb-2">Cross-reference entry</p>
              <p className="text-[15px] text-ink leading-relaxed">
                {headingNote || `This ECCN is retained on the Commerce Control List as a cross-reference. See the source on eCFR for jurisdictional details.`}
              </p>
            </aside>
          </div>
        )}
      </section>

      {/* Related ECCNs */}
      {entry?.related_eccns?.length > 0 && (
        <section className="border-t border-hairline bg-ivory-alt" data-testid="ccl-entry-related">
          <div className="max-w-5xl mx-auto px-6 lg:px-8 py-10">
            <Eyebrow>Related ECCNs</Eyebrow>
            <h2 className="font-serif text-xl text-ink mt-3 mb-5">ECCNs referenced in this entry</h2>
            <ul className="flex flex-wrap gap-x-4 gap-y-2">
              {entry.related_eccns.map((r) => (
                <li key={r}>
                  <Link
                    to={`/ccl/e/${r}`}
                    className="inline-flex items-center gap-1.5 font-mono text-[13px] font-semibold text-ink border-b border-brass/50 pb-0.5 hover:text-brass hover:border-brass transition-colors"
                    data-testid={`ccl-entry-related-${r}`}
                  >
                    {r}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Persistent disclaimer banner — bottom */}
      <section className="bg-ink text-ivory" data-testid="ccl-entry-banner-bottom">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-12 text-center">
          <p className="text-[11px] uppercase tracking-[0.28em] text-brass-light font-semibold mb-4">Verify Before Relying</p>
          <p className="font-serif text-xl sm:text-2xl leading-snug tracking-tight">
            This is a mirror of the ECCN text from eCFR{sourceAsOf !== "—" ? ` as of ${sourceAsOf}` : ""}. Nothing on this page is legal advice.
          </p>
          {entry?.source_url && (
            <a
              href={entry.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-8 bg-ivory text-ink px-6 py-3 text-[11px] tracking-[0.22em] uppercase font-semibold hover:bg-brass hover:text-ivory transition-colors"
              data-testid="ccl-entry-source-bottom"
            >
              View {entry?.eccn} on eCFR
              <ExternalLink className="w-4 h-4" strokeWidth={2} />
            </a>
          )}
        </div>
      </section>
    </Layout>
  );
}

function stripQuotes(s) {
  return String(s).replace(/["'"'"]/g, "");
}
